import React, { useCallback } from "react";
import { Copy } from "lucide-react";
import {
  InvoiceData,
  InvoiceItemData,
  TaxRate
} from "../../../../../types/invoices";
import { BookingV4 } from "../../../../../types/bookings";
import { Input } from "../../../../ui/input";
import { EDSButton } from "../../../../eds/EDSButton";
import { EDSDatePicker } from "../../../../eds/EDSDatePicker";
import InvoiceItemsTable from "./InvoiceItemsTable";

interface InvoiceEditorProps {
  invoice: InvoiceData;
  booking?: BookingV4;
  taxRates: TaxRate[];
  vatLabel: string;
  currencySymbol: string;
  isEditing: boolean;
  onUpdateInvoice: (updates: Partial<InvoiceData>) => void;
  onUpdateItem: (index: number, updates: Partial<InvoiceItemData>) => void;
  onRemoveItem: (index: number) => void;
  onAddBlankItem: () => void;
}

const InvoiceEditor = ({
  invoice,
  booking,
  taxRates,
  vatLabel,
  currencySymbol,
  isEditing,
  onUpdateInvoice,
  onUpdateItem,
  onRemoveItem,
  onAddBlankItem
}: InvoiceEditorProps): React.JSX.Element => {
  const invoiceDate = invoice.date ? new Date(invoice.date) : new Date();

  // Customer name from booking (read-only, matches collins-ui "Customer" column)
  const customerName = booking
    ? `${booking.first_name ?? ""} ${booking.last_name ?? ""}`.trim()
    : "";

  // Reference placeholder mirrors collins-ui: INV-{booking_id}
  const referencePlaceholder = booking?.booking_id
    ? `INV-${booking.booking_id}`
    : "";

  const handleDateChange = (date: Date | undefined): void => {
    if (date) {
      onUpdateInvoice({ date: date.toISOString() });
    }
  };

  // Check if booking has company address data to copy
  const hasBookingCompanyAddress =
    !!booking?.company ||
    !!booking?.company_address?.street ||
    !!booking?.company_address?.city ||
    !!booking?.company_address?.post_code;

  const handleCopyAddressFromBooking = useCallback((): void => {
    if (!booking) return;
    const companyAddress = booking.company_address;
    onUpdateInvoice({
      company: booking.company ?? "",
      street: companyAddress?.street ?? "",
      city: companyAddress?.city ?? "",
      post_code: companyAddress?.post_code ?? ""
    });
  }, [booking, onUpdateInvoice]);

  return (
    <div className="flex flex-col gap-4">
      <div className="pb-3 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="invoice-date"
              className="text-sm font-medium text-muted-foreground"
            >
              Invoice Date
            </label>
            {isEditing ? (
              <EDSDatePicker
                id="invoice-date"
                date={invoiceDate}
                onSelect={handleDateChange}
                placeholder="Select invoice date"
              />
            ) : (
              <span className="text-sm">
                {invoiceDate.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric"
                })}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="invoice-custom-ref"
              className="text-sm font-medium text-muted-foreground"
            >
              Reference
            </label>
            {isEditing ? (
              <Input
                id="invoice-custom-ref"
                value={invoice.custom_ref ?? ""}
                onChange={(e) =>
                  onUpdateInvoice({ custom_ref: e.target.value })
                }
                placeholder={referencePlaceholder}
                className="text-sm"
              />
            ) : (
              <span className="text-sm">{invoice.custom_ref ?? "-"}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="invoice-client-ref"
              className="text-sm font-medium text-muted-foreground"
            >
              Client Reference
            </label>
            {isEditing ? (
              <Input
                id="invoice-client-ref"
                value={invoice.client_ref ?? ""}
                onChange={(e) =>
                  onUpdateInvoice({ client_ref: e.target.value })
                }
                className="text-sm"
              />
            ) : (
              <span className="text-sm">{invoice.client_ref ?? "-"}</span>
            )}
          </div>
        </div>
      </div>

      <div className="pb-3 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-muted-foreground">
            Company Details
          </h4>
          {isEditing && hasBookingCompanyAddress && (
            <EDSButton
              variant="outline"
              size="sm"
              onClick={handleCopyAddressFromBooking}
              aria-label="Copy address from booking"
            >
              <Copy className="w-3 h-3" />
              Copy from booking
            </EDSButton>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <span
              id="invoice-customer-label"
              className="text-sm font-medium text-muted-foreground"
            >
              Customer
            </span>
            <span
              id="invoice-customer"
              aria-labelledby="invoice-customer-label"
              className="text-sm"
            >
              {customerName || "-"}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="invoice-company"
              className="text-sm font-medium text-muted-foreground"
            >
              Company
            </label>
            {isEditing ? (
              <Input
                id="invoice-company"
                value={invoice.company ?? ""}
                onChange={(e) => onUpdateInvoice({ company: e.target.value })}
                placeholder="Company name"
                className="text-sm"
              />
            ) : (
              <span className="text-sm">{invoice.company ?? "-"}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="invoice-street"
              className="text-sm font-medium text-muted-foreground"
            >
              Street
            </label>
            {isEditing ? (
              <Input
                id="invoice-street"
                value={invoice.street ?? ""}
                onChange={(e) => onUpdateInvoice({ street: e.target.value })}
                placeholder="Street address"
                className="text-sm"
              />
            ) : (
              <span className="text-sm">{invoice.street ?? "-"}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="invoice-city"
              className="text-sm font-medium text-muted-foreground"
            >
              City
            </label>
            {isEditing ? (
              <Input
                id="invoice-city"
                value={invoice.city ?? ""}
                onChange={(e) => onUpdateInvoice({ city: e.target.value })}
                placeholder="City"
                className="text-sm"
              />
            ) : (
              <span className="text-sm">{invoice.city ?? "-"}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="invoice-post-code"
              className="text-sm font-medium text-muted-foreground"
            >
              Post Code
            </label>
            {isEditing ? (
              <Input
                id="invoice-post-code"
                value={invoice.post_code ?? ""}
                onChange={(e) => onUpdateInvoice({ post_code: e.target.value })}
                placeholder="Post code"
                className="text-sm"
              />
            ) : (
              <span className="text-sm">{invoice.post_code ?? "-"}</span>
            )}
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-3 text-muted-foreground">
          Invoice Items
        </h4>
        <InvoiceItemsTable
          items={invoice.items ?? []}
          taxRates={taxRates}
          vatLabel={vatLabel}
          currencySymbol={currencySymbol}
          isEditing={isEditing}
          onUpdateItem={onUpdateItem}
          onRemoveItem={onRemoveItem}
          onAddBlankItem={onAddBlankItem}
        />
      </div>
    </div>
  );
};

export default InvoiceEditor;
